import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';

// Types
export interface CryptoQuotation {
  symbol: string;      // Primary field (mapped from backend)
  currency?: string;   // Legacy field for backward compatibility
  priceUsd: number;
  priceBrl: number;
  change24h: number;
  provider?: string;
  timestamp?: string;
  cached?: boolean;
}

export interface CryptoWallet {
  id: string;
  network: string;
  address: string;
  label?: string;
  balance: number;
  balanceUsd: number;
  createdAt: string;
}

export interface SupportedCrypto {
  symbol: string;
  name: string;
  networks: string[];
}

export interface NetworkInfo {
  network: string;
  name: string;
  chainId?: number;
  nativeCurrency?: string;
}

export interface QuotationRequest {
  network: string;
  cryptoSymbol: string;
  amountBrl: number;
}

export interface QuotationResponse {
  id: string;
  network: string;
  cryptoSymbol: string;
  amountBrl: number;
  amountCrypto: number;
  rate: number;
  fees: {
    network: number;
    platform: number;
    total: number;
  };
  expiresAt: string;
}

export interface PurchaseRequest {
  quotationId: string;
  destinationAddress: string;
  agentId?: string;
}

export interface PurchaseResponse {
  id: string;
  status: string;
  amountBrl: number;
  amountCrypto: number;
  network: string;
  cryptoSymbol: string;
  destinationAddress: string;
  txHash?: string;
  createdAt: string;
}

export interface CryptoPurchase {
  id: string;
  status: string;
  network: string;
  cryptoSymbol: string;
  amountBrl: number;
  amountCrypto: number;
  destinationAddress: string;
  txHash?: string;
  createdAt: string;
}

export interface SaleRequest {
  type: 'PLATFORM' | 'P2P';
  network: string;
  cryptoSymbol: string;
  amountCrypto: number;
  pricePerUnit?: number; // For P2P
  buyerAccountId?: string;
}

export interface CryptoSale {
  id: string;
  type: 'PLATFORM' | 'P2P';
  status: string;
  network: string;
  cryptoSymbol: string;
  amountCrypto: number;
  amountBrl: number;
  pricePerUnit?: number;
  createdAt: string;
}

export interface RegisterWalletRequest {
  network: string;
  address: string;
  label?: string;
}

// ==================== HELPER: Get Primary Virtual Account ====================

export function usePrimaryVirtualAccount() {
  return useQuery<{ id: string; type: string } | null>({
    queryKey: ['banking', 'primary-account'],
    queryFn: async () => {
      try {
        const response = await api.get(ENDPOINTS.banking.myAccounts);
        const accounts = response.data.accounts || [];
        // Find primary virtual account
        const primary = accounts.find((a: { isPrimary: boolean; type: string }) => a.isPrimary && a.type === 'VIRTUAL');
        if (primary) return { id: primary.id, type: primary.type };
        // Fallback to first virtual account
        const firstVirtual = accounts.find((a: { type: string }) => a.type === 'VIRTUAL');
        if (firstVirtual) return { id: firstVirtual.id, type: firstVirtual.type };
        // Fallback to any account
        return accounts[0] ? { id: accounts[0].id, type: accounts[0].type } : null;
      } catch {
        return null;
      }
    },
    staleTime: 60000, // Cache for 1 minute
  });
}

// ==================== QUOTATIONS ====================

export function useCryptoQuotations() {
  return useQuery<CryptoQuotation[]>({
    queryKey: ['crypto', 'quotations'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.crypto.quotationsPublic);
      const data = response.data.quotations || response.data || [];
      
      // Normalize data to ensure both symbol and currency are available
      return data.map((q: any) => ({
        symbol: q.symbol || q.currency,
        currency: q.currency || q.symbol, // Backward compatibility
        priceUsd: q.priceUsd || 0,
        priceBrl: q.priceBrl || 0,
        change24h: q.change24h || 0,
        provider: q.provider || 'CoinGecko',
        timestamp: q.timestamp || new Date().toISOString(),
        cached: q.cached ?? false,
      }));
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 10000,
  });
}

// ==================== SUPPORTED CRYPTOS & NETWORKS ====================

export function useSupportedCryptos(virtualAccountId: string) {
  return useQuery<SupportedCrypto[]>({
    queryKey: ['crypto', 'supported', virtualAccountId],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.crypto.supportedCryptos(virtualAccountId));
      return response.data;
    },
    enabled: !!virtualAccountId,
  });
}

export function useSupportedNetworks(virtualAccountId: string) {
  return useQuery<NetworkInfo[]>({
    queryKey: ['crypto', 'networks', virtualAccountId],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.crypto.networks(virtualAccountId));
      return response.data;
    },
    enabled: !!virtualAccountId,
  });
}

// ==================== WALLETS ====================

export function useCryptoWallets(virtualAccountId: string) {
  return useQuery<CryptoWallet[]>({
    queryKey: ['crypto', 'wallets', virtualAccountId],
    queryFn: async () => {
      if (!virtualAccountId) return [];
      
      try {
        const response = await api.get(ENDPOINTS.crypto.wallets(virtualAccountId));
        const data = response.data;
        
        // Handle different response formats
        if (Array.isArray(data)) return data;
        if (data?.wallets && Array.isArray(data.wallets)) return data.wallets;
        return [];
      } catch (error) {
        console.warn('Failed to fetch crypto wallets:', error);
        return [];
      }
    },
    enabled: !!virtualAccountId,
  });
}

export function useRegisterWallet(virtualAccountId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: RegisterWalletRequest) => {
      const response = await api.post(ENDPOINTS.crypto.registerWallet(virtualAccountId), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crypto', 'wallets', virtualAccountId] });
    },
  });
}

// ==================== QUOTATION & PURCHASE ====================

export function useGetQuotation(virtualAccountId: string) {
  return useMutation<QuotationResponse, Error, QuotationRequest>({
    mutationFn: async (data) => {
      const response = await api.post(ENDPOINTS.crypto.quotation(virtualAccountId), {
        network: data.network,
        cryptoSymbol: data.cryptoSymbol,
        amountBrl: String(Math.round(data.amountBrl * 100)), // Convert to centavos as string
      });
      return response.data;
    },
  });
}

export function useCryptoPurchase(virtualAccountId: string) {
  const queryClient = useQueryClient();
  
  return useMutation<PurchaseResponse, Error, PurchaseRequest>({
    mutationFn: async (data) => {
      const response = await api.post(ENDPOINTS.crypto.purchase(virtualAccountId), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crypto', 'wallets', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['crypto', 'purchases', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['virtual-account', 'balance'] });
    },
  });
}

export function useCryptoPurchases(virtualAccountId: string, filters?: { status?: string; network?: string }) {
  return useQuery<CryptoPurchase[]>({
    queryKey: ['crypto', 'purchases', virtualAccountId, filters],
    queryFn: async () => {
      if (!virtualAccountId) return [];
      
      try {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.network) params.append('network', filters.network);
        
        const response = await api.get(`${ENDPOINTS.crypto.purchases(virtualAccountId)}?${params}`);
        const data = response.data;
        
        // Handle different response formats
        if (Array.isArray(data)) return data;
        if (data?.purchases && Array.isArray(data.purchases)) return data.purchases;
        return [];
      } catch (error) {
        console.warn('Failed to fetch crypto purchases:', error);
        return [];
      }
    },
    enabled: !!virtualAccountId,
  });
}

// ==================== SALES ====================

export function useCreateSale(virtualAccountId: string) {
  const queryClient = useQueryClient();
  
  return useMutation<CryptoSale, Error, SaleRequest>({
    mutationFn: async (data) => {
      const response = await api.post(ENDPOINTS.crypto.createSale(virtualAccountId), {
        ...data,
        amountCrypto: String(data.amountCrypto),
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crypto', 'sales', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['crypto', 'wallets', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
    },
  });
}

export function useCryptoSales(virtualAccountId: string, filters?: { type?: string; status?: string }) {
  return useQuery<CryptoSale[]>({
    queryKey: ['crypto', 'sales', virtualAccountId, filters],
    queryFn: async () => {
      if (!virtualAccountId) return [];
      
      try {
        const params = new URLSearchParams();
        if (filters?.type) params.append('type', filters.type);
        if (filters?.status) params.append('status', filters.status);
        
        const response = await api.get(`${ENDPOINTS.crypto.sales(virtualAccountId)}?${params}`);
        const data = response.data;
        
        // Handle different response formats
        if (Array.isArray(data)) return data;
        if (data?.sales && Array.isArray(data.sales)) return data.sales;
        return [];
      } catch (error) {
        console.warn('Failed to fetch crypto sales:', error);
        return [];
      }
    },
    enabled: !!virtualAccountId,
  });
}

// ==================== P2P MARKETPLACE ====================

export function useP2PMarketplace(filters?: { cryptoSymbol?: string; network?: string }) {
  return useQuery<P2POffer[]>({
    queryKey: ['crypto', 'marketplace', filters],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (filters?.cryptoSymbol) params.append('cryptoSymbol', filters.cryptoSymbol);
        if (filters?.network) params.append('network', filters.network);
        
        const response = await api.get(`${ENDPOINTS.crypto.marketplace}?${params}`);
        const data = response.data;
        
        // Handle different response formats
        if (Array.isArray(data)) return data;
        if (data?.sales && Array.isArray(data.sales)) return data.sales;
        return [];
      } catch (error) {
        console.warn('Failed to fetch P2P marketplace:', error);
        return [];
      }
    },
  });
}

export function useAcceptP2PSale(virtualAccountId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (saleId: string) => {
      const response = await api.post(ENDPOINTS.crypto.acceptSale(virtualAccountId), { saleId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crypto', 'marketplace'] });
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
    },
  });
}

// ==================== PORTFOLIO CALCULATION ====================

export function useCryptoPortfolio(virtualAccountId: string) {
  const { data: wallets = [], isLoading: walletsLoading } = useCryptoWallets(virtualAccountId);
  const { data: quotations = [], isLoading: quotationsLoading } = useCryptoQuotations();
  
  const portfolio = {
    totalUsd: 0,
    totalBrl: 0,
    change24h: 0,
    breakdown: [] as { currency: string; percentage: number; valueUsd: number; valueBrl: number }[],
  };
  
  if (wallets.length > 0 && quotations.length > 0) {
    // Calculate total value
    wallets.forEach(wallet => {
      const quote = quotations.find(q => q.currency === wallet.network.split('_')[0] || q.currency === 'USDT');
      if (quote) {
        portfolio.totalUsd += wallet.balanceUsd;
        portfolio.totalBrl += wallet.balanceUsd * (quote.priceBrl / quote.priceUsd);
      }
    });
    
    // Calculate breakdown
    wallets.forEach(wallet => {
      if (wallet.balanceUsd > 0) {
        portfolio.breakdown.push({
          currency: wallet.network,
          percentage: (wallet.balanceUsd / portfolio.totalUsd) * 100,
          valueUsd: wallet.balanceUsd,
          valueBrl: wallet.balanceUsd * 5, // Approximate
        });
      }
    });
    
    // Calculate weighted average change
    if (portfolio.totalUsd > 0) {
      let weightedChange = 0;
      wallets.forEach(wallet => {
        const quote = quotations.find(q => q.currency === wallet.network.split('_')[0]);
        if (quote) {
          weightedChange += (wallet.balanceUsd / portfolio.totalUsd) * quote.change24h;
        }
      });
      portfolio.change24h = weightedChange;
    }
  }
  
  return {
    portfolio,
    isLoading: walletsLoading || quotationsLoading,
  };
}


// ==================== ADDITIONAL TYPES ====================

export type CryptoNetwork = 'ETHEREUM' | 'BSC' | 'POLYGON' | 'BITCOIN' | 'SOLANA';

export interface CryptoOperation {
  id: string;
  type: 'PURCHASE' | 'SALE' | 'DEPOSIT' | 'WITHDRAW' | 'TRANSFER_IN' | 'TRANSFER_OUT';
  crypto: string;
  amount: number;
  valueBrl: number;
  fees?: number;
  status: 'COMPLETED' | 'PENDING' | 'FAILED';
  txHash?: string;
  createdAt: string;
}

// Extended P2POffer for marketplace
export interface P2POffer {
  id: string;
  sellerId: string;
  sellerName: string;
  sellerRating?: number;
  sellerTrades?: number;
  type: 'BUY' | 'SELL';
  crypto: string;
  cryptoSymbol: string;
  network: string;
  availableAmount: number;
  amountCrypto: number;
  pricePerUnit: number;
  totalBrl: number;
  minAmount?: number;
  maxAmount?: number;
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  createdAt: string;
}

export interface CreateWalletRequest {
  network: CryptoNetwork;
  label?: string;
}

// ==================== WALLET CREATION ====================

export function useCreateWallet(virtualAccountId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateWalletRequest) => {
      const response = await api.post(ENDPOINTS.crypto.wallets(virtualAccountId), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crypto', 'wallets', virtualAccountId] });
    },
  });
}

// ==================== CRYPTO HISTORY ====================

export function useCryptoHistory(virtualAccountId: string, filters?: { type?: string; status?: string; period?: string; search?: string }) {
  return useQuery<CryptoOperation[]>({
    queryKey: ['crypto', 'history', virtualAccountId, filters],
    queryFn: async () => {
      if (!virtualAccountId) return [];
      
      try {
        const params = new URLSearchParams();
        if (filters?.type) params.append('type', filters.type);
        if (filters?.status) params.append('status', filters.status);
        if (filters?.period) params.append('period', filters.period);
        if (filters?.search) params.append('search', filters.search);
        
        const response = await api.get(`${ENDPOINTS.crypto.purchases(virtualAccountId)}/history?${params}`);
        const data = response.data;
        
        // Handle different response formats
        if (Array.isArray(data)) return data;
        if (data?.operations && Array.isArray(data.operations)) return data.operations;
        return [];
      } catch (error) {
        console.warn('Failed to fetch crypto history:', error);
        return [];
      }
    },
    enabled: !!virtualAccountId,
  });
}

// ==================== P2P OFFERS ====================

export function useP2POffers(filters?: { crypto?: string; type?: string }) {
  return useQuery<P2POffer[]>({
    queryKey: ['crypto', 'p2p-offers', filters],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (filters?.crypto) params.append('crypto', filters.crypto);
        if (filters?.type) params.append('type', filters.type);
        
        const response = await api.get(`${ENDPOINTS.crypto.marketplace}?${params}`);
        const data = response.data;
        
        // Handle different response formats
        if (Array.isArray(data)) return data;
        if (data?.offers && Array.isArray(data.offers)) return data.offers;
        if (data?.sales && Array.isArray(data.sales)) return data.sales;
        return [];
      } catch (error) {
        console.warn('Failed to fetch P2P offers:', error);
        return [];
      }
    },
  });
}

export function useMyP2POffers(virtualAccountId: string) {
  return useQuery<P2POffer[]>({
    queryKey: ['crypto', 'my-p2p-offers', virtualAccountId],
    queryFn: async () => {
      if (!virtualAccountId) return [];
      
      try {
        const response = await api.get(`${ENDPOINTS.crypto.sales(virtualAccountId)}?type=P2P`);
        const data = response.data;
        
        // Handle different response formats
        if (Array.isArray(data)) return data;
        if (data?.sales && Array.isArray(data.sales)) return data.sales;
        return [];
      } catch (error) {
        console.warn('Failed to fetch my P2P offers:', error);
        return [];
      }
    },
    enabled: !!virtualAccountId,
  });
}

export function useAcceptP2POffer(virtualAccountId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { offerId: string; amount: number }) => {
      const response = await api.post(ENDPOINTS.crypto.acceptSale(virtualAccountId), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crypto', 'p2p-offers'] });
      queryClient.invalidateQueries({ queryKey: ['crypto', 'wallets', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
    },
  });
}

export function useCancelP2POffer(virtualAccountId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (offerId: string) => {
      const response = await api.delete(`${ENDPOINTS.crypto.sales(virtualAccountId)}/${offerId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crypto', 'my-p2p-offers', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['crypto', 'p2p-offers'] });
    },
  });
}
